import axios from 'axios';

import { Supervisor, BatchID, EmployeeTimeSheet, ExportPayload } from "../interfaces/V1Interface";

// Helper function to get authentication token
const getAuthToken = async () => {
  try {
    const tokenUrl = "https://admin.api.wcdp.com/adminApi/User/AdminLogin";
    const employeeId = "27016";
    const password = "27016";

    const tokenResponse = await axios.post(tokenUrl, { employeeId, password });
    return tokenResponse.data.token;
  } catch (error) {
    console.error('Error getting authentication token:', error);
    throw error;
  }
};

// API call to get list of supervisors for a week
export const getSupervisorsForWeek = async (timeSheetDate: string, sdate: string, edate: string) => {
  try {
    const token = await getAuthToken();
    const url = `https://admin.api.wcdp.com/adminApi/Employee/GetSupervList?TimeSheetDate=${timeSheetDate}&Sdate=${sdate}&Edate=${edate}`;

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching supervisors:', error);
    throw error;
  }
};

// API call to get batch IDs
export const getBatchIDs = async (batchPrefix: string) => {
  try {
    const token = await getAuthToken();
    const url = `https://admin.api.wcdp.com/adminApi/Payroll/GetBatchIDs?batchPrefix=${batchPrefix}`;

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data.data as BatchID[];
  } catch (error) {
    console.error('Error fetching batch IDs:', error);
    throw error;
  }
};

// Helper function to determine next available batch number
export const getNextBatchNumber = (batchIDs: BatchID[]): number => {
  const numbers: number[] = [];

  batchIDs.forEach(batch => {
    const match = batch.BatchId.match(/\s(\d+)(DD)?$/);
    if (match && match[1]) {
      numbers.push(parseInt(match[1], 10));
    }
  });

  if (numbers.length === 0) return 1;

  const maxNumber = Math.max(...numbers);
  return maxNumber + 1;
};

// API call to get list of employees
export const getEmployeesBySupervisor = async (
  supervisorId: string,
  sdate: string,
  edate: string,
  timeSheetDate: string = "",
  search: string = "",
  conditionId: string = "0",
  pageNumber: number = 0
) => {
  try {
    const token = await getAuthToken();
    const url = "https://admin.api.wcdp.com/adminApi/Employee/GetEmployeeTimeSheetBySupervisor";

    const payload = {
      supervisorId,
      timeSheetDate,
      search,
      sdate,
      edate,
      conditionId,
      pageNumber,
    };

    const response = await axios.post(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data.data;
  } catch (error) {
    console.error('Error fetching employees:', error);
    throw error;
  }
};

// API call to export payroll data (Paint)
export const exportPayrollDataPaint = async (
  batchId: string,
  supervisorId: string,
  startDate: string,
  endDate: string,
  employeeIDs?: string[]
) => {
  try {
    const token = await getAuthToken();
    const url = "https://admin.api.wcdp.com/adminApi/Payroll/ExportPayrollData";

    const payload = {
      batchId,
      supervisorId,
      startDate,
      endDate,
      employeeIds: employeeIDs?.join(',') || '',
    };

    const response = await axios.get(url, {
      params: payload,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error exporting paint payroll data:', error);
    throw error;
  }
};

// API call to export payroll data (Drywall)
export const exportPayrollDataDrywall = async (
  batchId: string,
  supervisorId: string,
  startDate: string,
  endDate: string,
  employeeIDs?: string[]
) => {
  try {
    const token = await getAuthToken();
    const url = "https://admin.api.wcdp.com/adminApi/Payroll/ExportPayrollDataDrywall";

    const payload = {
      batchId,
      supervisorId,
      startDate,
      endDate,
      employeeIds: employeeIDs?.join(',') || '',
    };

    const response = await axios.get(url, {
      params: payload,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error exporting drywall payroll data:', error);
    throw error;
  }
};

// API call to get batch report
export const getBatchReport = async (batchId: string) => {
  try {
    const token = await getAuthToken();
    const url = "https://admin.api.wcdp.com/adminApi/Payroll/GetBatchReport";

    // Get both regular and DD reports
    const regularResponse = await axios.get(url, {
      params: { batchId },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const ddResponse = await axios.get(url, {
      params: { batchId: `${batchId}DD` },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return {
      regularReport: regularResponse.data,
      ddReport: ddResponse.data,
    };
  } catch (error) {
    console.error('Error fetching batch reports:', error);
    throw error;
  }
};

// API call to print timecards for a supervisor
export const printTimecards = async (
  supervisorId: string,
  startDate: string,
  endDate: string
) => {
  try {
    const token = await getAuthToken();
    const url = "https://admin.api.wcdp.com/adminApi/Employee/DownloadFileAsyncSupervisor";

    const response = await axios.get(url, {
      params: {
        supervisorId,
        startDate,
        endDate,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error printing timecards:', error);
    throw error;
  }
};